import { useEffect, useMemo, useState } from 'react';
import { parseRichText, type Inline } from '@machs/shared';
import { api } from './api';
import { useSync } from './sync';

/** Person, die sich per @ erwähnen lässt: Teammitglied oder Externe. */
export interface Person {
  id: string;
  name: string;
  external: boolean;
  /** Aufgabe bzw. Projekt der Freigabe, damit Passende oben stehen */
  taskId?: string | null;
  projectId?: string | null;
}

interface GuestRow {
  userId: string;
  name: string;
  taskId: string | null;
  projectId: string | null;
}

// Einmal je Sitzung laden; die Liste ändert sich selten
let guestCache: Promise<GuestRow[]> | null = null;

/**
 * Alle bekannten Personen: das Team, Externe aus Freigaben und Externe, die
 * schon geschrieben haben. Gleiche Namen erscheinen nur einmal, Mitglieder
 * haben Vorrang.
 */
export function usePeople(): Person[] {
  const { client, me, version } = useSync();
  const [guests, setGuests] = useState<GuestRow[]>([]);

  useEffect(() => {
    if (me.guest) return;
    guestCache ??= api<{ guests: GuestRow[] }>('/shares/people').then((r) => r.guests, () => ((guestCache = null), []));
    let alive = true;
    void guestCache.then((g) => alive && setGuests(g));
    return () => {
      alive = false;
    };
  }, [me.guest]);

  return useMemo(() => {
    const out = new Map<string, Person>();
    const add = (p: Person) => {
      const key = p.name.trim().toLowerCase();
      if (key && !out.has(key)) out.set(key, p);
    };
    for (const m of me.members) add({ id: m.userId, name: m.name, external: false });
    for (const g of guests) add({ id: g.userId, name: g.name, external: true, taskId: g.taskId, projectId: g.projectId });
    for (const m of client.data.message.values()) {
      if (m.authorKind === 'guest' && m.authorId) add({ id: m.authorId, name: m.authorName, external: true, taskId: m.taskId });
    }
    return [...out.values()];
  }, [client, version, me.members, guests]);
}

/** IDs der Personen, die im Text per @Name erwähnt sind. */
export function mentionIds(text: string, people: Person[]): string[] {
  const ids = new Set<string>();
  const walk = (nodes: Inline[]) => {
    for (const n of nodes) {
      if (n.t === 'mention') ids.add(n.id);
      else if (n.t === 'b' || n.t === 'i' || n.t === 's') walk(n.c);
    }
  };
  for (const b of parseRichText(text, { mentions: people })) {
    if (b.t === 'lines' || b.t === 'quote') b.lines.forEach(walk);
    else if (b.t === 'ul' || b.t === 'ol') b.items.forEach(walk);
  }
  return [...ids];
}
